import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';

import Button from 'shopper/components/Button';

import ErrorBoundary from 'components/ErrorBoundary';
import GenericError from 'components/GenericError';
import SkeletonGroup from 'components/Skeleton/SkeletonGroup';
import OfferCard from 'components/Timeline/OfferCard/V2/OfferGridCard';

import useInfiniteQuery from 'hooks/useInfiniteQuery';
import useIntersectionObserver from 'hooks/useIntersectionObserver';

import { findAll } from 'lib/array';
import { sendEvent } from 'lib/gtag';
import { isOfferFinished } from 'lib/offer';
import placeholder from 'lib/placeholder';

const EmptyList = dynamic(() => import('./EmptyList'));
const OfferCardSkeleton = dynamic(
  () => import('components/Timeline/OfferCard/V2/OfferGridCard/Skeleton'),
  { ssr: false }
);

const OffersList = ({
  cardTitleAs = 'h2',
  gaEventCategory,
  cardsAds = [],
  hasMoreInitialOffers = false,
  queryFn,
  queryKey,
  initialOffers,
  fixedOffer = null,
}) => {
  const router = useRouter();
  const [isPaginationEnabled, setPaginationEnabled] = useState(false);
  const fetchNextPageRef = useRef(null);

  const {
    data: offers,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isSuccess,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey,
    queryFn: (after, { signal }) => queryFn({ after }, { signal }),
    getNextPageParam: (lastOffers) => lastOffers?.after,

    select: (_offers) => {
      const activeOffers = [];

      for (let i = 0; i < _offers.length; i++) {
        const item = _offers[i];

        if (!isOfferFinished(item)) {
          /**
           * Saves `cardAd` as a offer prop for perf as every `cardAd` can
           * be at any position at the list, so we would have to do double loops
           * every time this list is rendered otherwise
           */

          activeOffers.push({
            ...item,
            cardAds: findAll(cardsAds, (cardAd) => cardAd.position - 1 === i),
          });
        }
      }

      if (fixedOffer && !isOfferFinished(fixedOffer)) {
        return [fixedOffer, ...activeOffers];
      }

      return activeOffers;
    },
    flattenKey: 'offers',
    initialData: initialOffers,
    staleTime: 0,
    cacheTime: 0.5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    const resetInternalStates = () => {
      setPaginationEnabled(false);
    };

    router.events.on('routeChangeComplete', resetInternalStates);

    return () => {
      router.events.off('routeChangeComplete', resetInternalStates);
    };
  }, []);

  useIntersectionObserver({
    enabled: isPaginationEnabled && hasNextPage,
    target: fetchNextPageRef,
    rootMargin: '1200px',
    threshold: 0,
    onIntersect: fetchNextPage,
  });

  const onEnablePagination = () => {
    setPaginationEnabled(true);
    sendEvent({ action: 'see_more_offers', category: gaEventCategory });
  };

  const isListLoading = isFetchingNextPage || isLoading;
  // hasMoreInitialOffers guards against showing the button when the backend
  // returns a non-null cursor on the last page — clicking would fetch nothing.
  const showPaginate = !isListLoading && hasNextPage && hasMoreInitialOffers;

  if (isSuccess && offers.length === 0) {
    return <EmptyList gaEventCategory={gaEventCategory} />;
  }

  return (
    <ErrorBoundary
      fallback={
        <GenericError message={placeholder('errors.couldNotLoadOffers')} />
      }
    >
      <div className="grid grid-flow-row grid-cols-1 gap-4 lg:grid-cols-3">
        {isSuccess &&
          offers.map((offer, index) => (
            /**
             * API is currently sending duplicated `offer.key` for
             * some reason. Quickfix this on our side for now.
             */
            <OfferCard
              key={`${offer.key}-${index}`}
              cardTitleAs={cardTitleAs}
              gaEventCategory={gaEventCategory}
              {...offer}
            />
          ))}
        {showPaginate && (
          <div className="col-span-1 mt-4 lg:col-span-3 lg:mt-12">
            <Button
              className="mx-auto"
              size="size1"
              onClick={onEnablePagination}
            >
              {placeholder('actions.loadMoreOffers')}
            </Button>
          </div>
        )}
        {isListLoading && (
          <SkeletonGroup items={6} skeleton={OfferCardSkeleton} />
        )}
      </div>
      <div ref={fetchNextPageRef} />
    </ErrorBoundary>
  );
};

OffersList.propTypes = {
  cardTitleAs: PropTypes.string,
  hasMoreInitialOffers: PropTypes.bool,
  cardsAds: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string.isRequired,
      id: PropTypes.number.isRequired,
      image: PropTypes.string,
      isClosable: PropTypes.bool,
      mobileImage: PropTypes.string,
      // Used only for hardcoded card ad and are relevant for SEO
      text: PropTypes.string,
      url: PropTypes.string,
      // Used only for hardcoded card ad
      onClick: PropTypes.func,
    })
  ),
  fixedOffer: PropTypes.object,
  gaEventCategory: PropTypes.string,
  initialOffers: PropTypes.shape({
    after: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sort: PropTypes.string,
    before: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    offers: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        offerComments: PropTypes.number.isRequired,
        offerId: PropTypes.number.isRequired,
        offerIsHighlight: PropTypes.bool.isRequired,
        ratings: PropTypes.shape().isRequired,
        offerOldPrice: PropTypes.number,
        offerPhoto: PropTypes.string.isRequired,
        offerPrice: PropTypes.number.isRequired,
        offerPriceType: PropTypes.string.isRequired,
        offerPublished: PropTypes.string.isRequired,
        offerSlug: PropTypes.string.isRequired,
        offerStatusName: PropTypes.string.isRequired,
        offerUserVisibility: PropTypes.string.isRequired,
        offerTags: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string.isRequired,
            type: PropTypes.string.isRequired,
          })
        ),
        offerTitle: PropTypes.string.isRequired,
        storeDomain: PropTypes.string.isRequired,
      }).isRequired
    ).isRequired,
  }).isRequired,
  queryFn: PropTypes.func.isRequired,
  queryKey: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.shape({
        storeId: PropTypes.number,
        storeName: PropTypes.string,
      }),
    ])
  ).isRequired,
};

export default OffersList;
